import { PauseIcon, PlayIcon } from "@heroicons/react/24/solid";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  Square3Stack3DIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { getVideoSceneFrameUrl } from "@/api/renderer/api";
import { useGetVideoHeatmapRender, useGetVideoOriginalRender } from "@/api/renderer/queries";
import { VideoInsightScene } from "@/api/studio/insights/types/attention.type";
import { VideoLoadingAnimation } from "@/features/reports/components/in-depth/video-loading-animation";
import { cn } from "@/utils/classes";
import { asPlaybackTime } from "@/utils/format";

type RenderVariant = "original" | "heatmap";

interface ScenePreview {
  frameUrl: string;
  leftPercent: number;
  sceneNumber: number;
  time: number;
}

// 0-1 position across the whole scene bar (not just the hovered segment), so the darker
// seek-preview fill can span multiple segments between the current progress and the cursor
type HoverPosition = number;

/* Prop Types */
interface VideoPlayerProps {
  projectId: string;
  studyId: string;
  videoKey: string;
  scenes: VideoInsightScene[];
  selectedSceneKey: string | null;
}

const SKIP_SECONDS = 5;
const PLAYBACK_RATES = [1, 1.25, 1.5, 2];
// Matches the scene preview thumbnail's w-36 class, in px
const PREVIEW_WIDTH = 144;

/**
 * VideoPlayer component
 *
 * Fetches a pre-signed playback URL for the current video and renders it with a custom control
 * bar matching the in-depth Figma design: a per-scene segmented progress bar, play/rewind/forward/
 * mute controls, a timestamp, a playback speed toggle, and fullscreen.
 */
export const VideoPlayer = ({ projectId, studyId, videoKey, scenes, selectedSceneKey }: VideoPlayerProps) => {
  /* State */
  const [variant, setVariant] = useState<RenderVariant>("original");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [playbackRateIndex, setPlaybackRateIndex] = useState(0);
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scenePreview, setScenePreview] = useState<ScenePreview | null>(null);
  const [hoverPosition, setHoverPosition] = useState<HoverPosition | null>(null);

  /* Queries */
  const { data: originalRender, isLoading: isOriginalLoading, isError: isOriginalError } = useGetVideoOriginalRender({ projectId, studyId, assetKey: videoKey });
  // Fetched eagerly (not gated on the variant toggle) so the heatmap render's URL - and, via the
  // hidden preload <video> below, its actual bytes - are ready before the user ever switches to it
  const {
    data: heatmapRender,
    isLoading: isHeatmapLoading,
    isError: isHeatmapError,
  } = useGetVideoHeatmapRender({ projectId, studyId, assetKey: videoKey });

  const render = variant === "heatmap" ? heatmapRender : originalRender;
  const isLoading = variant === "heatmap" ? isHeatmapLoading : isOriginalLoading;
  const isError = variant === "heatmap" ? isHeatmapError : isOriginalError;

  /* Refs */
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneBarRef = useRef<HTMLDivElement>(null);
  const speedMenuRef = useRef<HTMLDivElement>(null);
  // Scenes whose frames have already been preloaded, so re-entering one doesn't re-issue requests
  const preloadedScenesRef = useRef<Set<string>>(new Set());
  // Frame URLs confirmed loaded, so we never show a blank/broken image for one still in flight
  const loadedFrameUrlsRef = useRef<Set<string>>(new Set());
  // Caps preview updates to one per animation frame, since mousemove fires far more often than that
  const previewFrameRafRef = useRef<number | null>(null);
  // Carries playback position/state across a render-variant swap, since changing the <video>'s
  // src otherwise resets currentTime to 0 the same way switching to a different video should.
  const resumeRef = useRef({ time: 0, wasPlaying: false });

  /* Handlers */
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  };

  const toggleVariant = () => {
    const video = videoRef.current;
    resumeRef.current = { time: video?.currentTime ?? 0, wasPlaying: !!video && !video.paused };
    setVariant((current) => (current === "original" ? "heatmap" : "original"));
  };

  const skip = (deltaSeconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(Math.max(video.currentTime + deltaSeconds, 0), video.duration || 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const nextVolume = Number(event.target.value);
    video.volume = nextVolume;
    video.muted = nextVolume === 0;
    setVolume(nextVolume);
    setIsMuted(nextVolume === 0);
  };

  const selectPlaybackRate = (index: number) => {
    const video = videoRef.current;
    if (video) video.playbackRate = PLAYBACK_RATES[index];
    setPlaybackRateIndex(index);
    setIsSpeedMenuOpen(false);
  };

  // Converts a click/hover x-position within an element to a 0-1 ratio across its width
  const ratioWithinElement = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
  };

  const seekWithinScene = (scene: VideoInsightScene, event: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = scene.startTime + ratioWithinElement(event) * scene.duration;
  };

  const seekWithinDuration = (event: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    video.currentTime = ratioWithinElement(event) * duration;
  };

  // Warms the browser's image cache for a scene's frames so hovering into it doesn't pay a
  // network + decode cost per frame while scrubbing (matches YouTube's storyboard preload).
  const preloadSceneFrames = (scene: VideoInsightScene) => {
    if (preloadedScenesRef.current.has(scene.scene)) return;
    preloadedScenesRef.current.add(scene.scene);

    scene.frames.forEach((frameName) => {
      const url = getVideoSceneFrameUrl({ projectId, studyId, assetKey: videoKey, frameName });
      const image = new Image();
      image.onload = () => loadedFrameUrlsRef.current.add(url);
      image.src = url;
    });
  };

  const cancelPendingPreview = () => {
    if (previewFrameRafRef.current !== null) {
      cancelAnimationFrame(previewFrameRafRef.current);
      previewFrameRafRef.current = null;
    }
  };

  const previewSceneFrame = (scene: VideoInsightScene, sceneIndex: number, event: React.MouseEvent<HTMLDivElement>) => {
    // Read from the (pooled) event synchronously - only the state update itself is deferred.
    // localRatio is the position within this scene's own segment (for picking its thumbnail
    // frame); the global ratio (below, once we have the bar's rect) is across the whole bar.
    const localRatio = ratioWithinElement(event);
    const clientX = event.clientX;

    if (previewFrameRafRef.current !== null) return;
    previewFrameRafRef.current = requestAnimationFrame(() => {
      previewFrameRafRef.current = null;

      const barRect = sceneBarRef.current?.getBoundingClientRect();

      // Drives the darker seek-preview fill, independent of whether a thumbnail is available/loaded
      if (barRect) setHoverPosition(Math.min(Math.max((clientX - barRect.left) / barRect.width, 0), 1));

      if (scene.frames.length === 0) {
        setScenePreview(null);
        return;
      }

      const frameIndex = Math.min(Math.floor(localRatio * scene.frames.length), scene.frames.length - 1);
      const frameUrl = getVideoSceneFrameUrl({ projectId, studyId, assetKey: videoKey, frameName: scene.frames[frameIndex] });

      // The frame hasn't finished loading yet - keep showing the last one rather than flash blank
      if (!loadedFrameUrlsRef.current.has(frameUrl) || !barRect) return;

      // Clamp by the preview's own half-width (not just a flat percentage) so it never overflows
      // past the player's edges - the player clips overflow, which otherwise crops it near either end
      const halfPreviewWidth = PREVIEW_WIDTH / 2;
      const leftPx = Math.min(Math.max(clientX - barRect.left, halfPreviewWidth), barRect.width - halfPreviewWidth);
      const leftPercent = (leftPx / barRect.width) * 100;
      const time = scene.startTime + localRatio * scene.duration;

      setScenePreview({ frameUrl, leftPercent, sceneNumber: sceneIndex + 1, time });
    });
  };

  // Same darker seek-preview fill as previewSceneFrame, for the fallback bar shown when there's no
  // per-scene data (no thumbnails available in that case, so nothing to preload/wait on)
  const previewDurationHover = (event: React.MouseEvent<HTMLDivElement>) => {
    const ratio = ratioWithinElement(event);

    if (previewFrameRafRef.current !== null) return;
    previewFrameRafRef.current = requestAnimationFrame(() => {
      previewFrameRafRef.current = null;
      setHoverPosition(ratio);
    });
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  };

  /* Side Effects */
  useEffect(() => {
    setCurrentTime(0);
    setIsPlaying(false);
    setVariant("original");
    resumeRef.current = { time: 0, wasPlaying: false };
    preloadedScenesRef.current = new Set();
  }, [videoKey]);

  useEffect(() => {
    return () => cancelPendingPreview();
  }, []);

  useEffect(() => {
    // Setting currentTime before the video's metadata has loaded is still honoured by the
    // browser once it does (it's recorded as the default playback start position), so no need to
    // wait for onLoadedMetadata here.
    if (!selectedSceneKey) return;
    const scene = scenes.find((s) => s.scene === selectedSceneKey);
    const video = videoRef.current;
    if (!scene || !video) return;
    video.currentTime = scene.startTime;
  }, [selectedSceneKey]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (!isSpeedMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(event.target as Node)) {
        setIsSpeedMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSpeedMenuOpen]);

  /* Render */
  return (
    <div ref={containerRef} className="relative w-full aspect-[895/441] rounded-lg bg-[#323c49] overflow-hidden min-h-[450px]">
      {isLoading && <VideoLoadingAnimation />}
      {isError && <p className="absolute inset-0 flex items-center justify-center text-sm text-white/60">Unable to load video. Please try again.</p>}

      {render && (
        <>
          <video
            key={videoKey}
            ref={videoRef}
            data-testid="video-player-element"
            src={render.url}
            className="absolute inset-0 w-full h-full object-contain cursor-pointer"
            onClick={togglePlay}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
            onLoadedMetadata={(event) => {
              const video = event.currentTarget;
              setDuration(video.duration);

              const { time, wasPlaying } = resumeRef.current;
              if (time > 0) {
                video.currentTime = time;
                if (wasPlaying) video.play();
                resumeRef.current = { time: 0, wasPlaying: false };
              }
            }}
          />

          {/* Warms the browser's cache for the heatmap render's actual video bytes (not just its
              URL) while the user is still watching the original, so switching to it is instant */}
          {heatmapRender && variant !== "heatmap" && (
            <video src={heatmapRender.url} muted preload="auto" aria-hidden="true" className="absolute w-0 h-0 opacity-0 pointer-events-none" />
          )}

          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-3 bg-gradient-to-t from-black/70 to-transparent rounded-b-lg">
            {/* Per-scene progress */}
            <div ref={sceneBarRef} data-testid="scene-progress-bar" className="relative flex gap-1 w-full">
              {scenePreview && (
                <div
                  className="absolute bottom-full mb-2 -translate-x-1/2 pointer-events-none w-36 rounded-md border border-white/20 shadow-lg overflow-hidden bg-[#1a1f27]"
                  style={{ left: `${scenePreview.leftPercent}%` }}
                >
                  <img src={scenePreview.frameUrl} alt="Scene preview" className="w-full h-auto block" />
                  <div className="flex items-center justify-between px-2 py-1 text-[11px] font-semibold text-white">
                    <span>Scene {scenePreview.sceneNumber}</span>
                    <span>{asPlaybackTime(scenePreview.time)}</span>
                  </div>
                </div>
              )}

              {scenes.length === 0 ? (
                (() => {
                  const playedPercent = duration ? (currentTime / duration) * 100 : 0;
                  const hoverPercent = hoverPosition !== null ? hoverPosition * 100 : 0;
                  const seekPreviewWidth = hoverPosition !== null && hoverPercent > playedPercent ? hoverPercent - playedPercent : 0;

                  return (
                    <div
                      role="button"
                      aria-label="Seek"
                      onClick={seekWithinDuration}
                      onMouseMove={previewDurationHover}
                      onMouseLeave={() => {
                        cancelPendingPreview();
                        setHoverPosition(null);
                      }}
                      className="relative h-1.5 w-full rounded-full bg-[#e9e9ec] overflow-hidden cursor-pointer transition-transform duration-150 hover:scale-y-150"
                    >
                      <div className="absolute inset-y-0 left-0 bg-[#f2318a]" style={{ width: `${playedPercent}%` }} />
                      {seekPreviewWidth > 0 && (
                        <div data-testid="scene-seek-preview" className="absolute inset-y-0 bg-[#c2185b]" style={{ left: `${playedPercent}%`, width: `${seekPreviewWidth}%` }} />
                      )}
                    </div>
                  );
                })()
              ) : (
                (() => {
                  const totalScenesDuration = scenes.reduce((sum, s) => sum + s.duration, 0);
                  // Each scene's [startRatio, endRatio) span across the whole bar, in playback order
                  const sceneRanges = scenes.reduce<{ startRatio: number; endRatio: number }[]>((ranges, scene) => {
                    const previousEnd = ranges.length > 0 ? ranges[ranges.length - 1].endRatio : 0;
                    return [...ranges, { startRatio: previousEnd, endRatio: previousEnd + scene.duration / totalScenesDuration }];
                  }, []);
                  const playedRatio = totalScenesDuration ? currentTime / totalScenesDuration : 0;

                  return scenes.map((scene, index) => {
                    const { startRatio, endRatio } = sceneRanges[index];
                    const playedPercent = currentTime <= scene.startTime ? 0 : currentTime >= scene.endTime ? 100 : ((currentTime - scene.startTime) / scene.duration) * 100;

                    // The seek-preview fill for this segment is the portion of [current progress,
                    // hovered position] that falls within it - spanning every segment in between,
                    // not just the one directly under the cursor.
                    let seekPreviewLeftPercent = 0;
                    let seekPreviewWidthPercent = 0;
                    if (hoverPosition !== null) {
                      const overlapStart = Math.max(startRatio, playedRatio);
                      const overlapEnd = Math.min(endRatio, hoverPosition);
                      if (overlapEnd > overlapStart) {
                        const segmentSpan = endRatio - startRatio;
                        seekPreviewLeftPercent = ((overlapStart - startRatio) / segmentSpan) * 100;
                        seekPreviewWidthPercent = ((overlapEnd - overlapStart) / segmentSpan) * 100;
                      }
                    }

                    return (
                      <div
                        key={scene.scene}
                        data-testid="scene-progress-segment"
                        role="button"
                        aria-label={`Seek within scene ${scene.scene}`}
                        onClick={(event) => seekWithinScene(scene, event)}
                        onMouseEnter={() => preloadSceneFrames(scene)}
                        onMouseMove={(event) => previewSceneFrame(scene, index, event)}
                        onMouseLeave={() => {
                          cancelPendingPreview();
                          setScenePreview(null);
                          setHoverPosition(null);
                        }}
                        className="relative h-1.5 rounded-full bg-[#e9e9ec] overflow-hidden cursor-pointer transition-transform duration-150 hover:scale-y-150"
                        style={{ width: `${(scene.duration / totalScenesDuration) * 100}%` }}
                      >
                        <div className="absolute inset-y-0 left-0 bg-[#f2318a]" style={{ width: `${playedPercent}%` }} />
                        {seekPreviewWidthPercent > 0 && (
                          <div
                            data-testid="scene-seek-preview"
                            className="absolute inset-y-0 bg-[#c2185b]"
                            style={{ left: `${seekPreviewLeftPercent}%`, width: `${seekPreviewWidthPercent}%` }}
                          />
                        )}
                      </div>
                    );
                  });
                })()
              )}
            </div>

            {/* Controls */}
            <div className="grid grid-cols-3 items-center w-full text-white">
              <div className="flex items-center gap-3 justify-self-start">
                <button
                  type="button"
                  onClick={togglePlay}
                  aria-label={isPlaying ? "Pause" : "Play"}
                  className="p-1.5 rounded-md cursor-pointer hover:bg-white/10 transition-colors"
                >
                  {isPlaying ? <PauseIcon className="size-[26px]" /> : <PlayIcon className="size-[26px]" />}
                </button>
                <button
                  type="button"
                  onClick={() => skip(-SKIP_SECONDS)}
                  aria-label="Rewind 5 seconds"
                  className="p-1.5 rounded-md cursor-pointer hover:bg-white/10 transition-colors"
                >
                  <ArrowUturnLeftIcon className="size-[18px]" />
                </button>
                <button
                  type="button"
                  onClick={() => skip(SKIP_SECONDS)}
                  aria-label="Forward 5 seconds"
                  className="p-1.5 rounded-md cursor-pointer hover:bg-white/10 transition-colors"
                >
                  <ArrowUturnRightIcon className="size-[18px]" />
                </button>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={toggleMute}
                    aria-label={isMuted ? "Unmute" : "Mute"}
                    className="p-1.5 rounded-md cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    {isMuted || volume === 0 ? <SpeakerXMarkIcon className="size-[18px]" /> : <SpeakerWaveIcon className="size-[18px]" />}
                  </button>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={volume}
                    onChange={handleVolumeChange}
                    aria-label="Volume"
                    className="w-16 accent-[#f2318a] cursor-pointer"
                  />
                </div>
              </div>

              <p className="justify-self-center text-sm font-medium whitespace-nowrap">
                {asPlaybackTime(currentTime)} / {asPlaybackTime(duration)}
              </p>

              <div className="flex items-center gap-3 justify-self-end">
                <div className="relative" ref={speedMenuRef}>
                  <button
                    type="button"
                    onClick={() => setIsSpeedMenuOpen((open) => !open)}
                    className="px-2 py-1.5 rounded-md text-xs font-semibold cursor-pointer hover:bg-white/10 transition-colors"
                    aria-label="Change playback speed"
                    aria-haspopup="listbox"
                    aria-expanded={isSpeedMenuOpen}
                  >
                    {PLAYBACK_RATES[playbackRateIndex]}x
                  </button>
                  {isSpeedMenuOpen && (
                    <ul role="listbox" className="absolute bottom-full right-0 mb-2 flex flex-col rounded-lg bg-[#242c35] py-1 text-xs shadow-lg">
                      {PLAYBACK_RATES.map((rate, index) => (
                        <li key={rate}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={index === playbackRateIndex}
                            onClick={() => selectPlaybackRate(index)}
                            className={cn("block w-full px-3 py-1.5 text-left whitespace-nowrap cursor-pointer hover:bg-white/10", index === playbackRateIndex && "text-[#f2318a]")}
                          >
                            {rate}x
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  onClick={toggleVariant}
                  aria-label={variant === "heatmap" ? "Switch to original video" : "Switch to attention heatmap"}
                  aria-pressed={variant === "heatmap"}
                  className="p-1.5 rounded-md cursor-pointer hover:bg-white/10 transition-colors"
                >
                  <Square3Stack3DIcon className={cn("size-[18px]", variant === "heatmap" ? "text-white" : "text-white/60")} />
                </button>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  className="p-1.5 rounded-md cursor-pointer hover:bg-white/10 transition-colors"
                >
                  {isFullscreen ? <ArrowsPointingInIcon className="size-[18px]" /> : <ArrowsPointingOutIcon className="size-[18px]" />}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
